import { TextFieldStyled } from '@nike/nike-design-system-components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useController } from 'react-hook-form';

import { localeInfo } from '../../../../constants/locale.const';
import { useGetProfile, useGetStoreInfo } from '../../../../globals';
import { getDecimalCount } from '../../../../utils/currencyUtils';
import { stringToFloat } from '../../../../utils/mathUtils';
import './Currency.css';

export function MultipleCurrencyInput({
  allowNegativeValue,
  control,
  id,
  isRequired,
  label,
  name,
  readOnly,
  supportText,
  supportTextColor,
  subTenderType,
}) {
  const {
    field: { onChange, onBlur, value },
    fieldState: { error },
  } = useController({
    control,
    defaultValue: '',
    name,
  });

  const [formattedValue, setFormattedValue] = useState(value);

  useEffect(() => {
    setFormattedValue(value);
  }, [value]);

  const { currency, locale } = useGetStoreInfo();
  const { country } = useGetProfile();

  // Get locale-specific separators from localeInfo
  const getLocaleSeparators = () => {
    const countryLocale = localeInfo[country];
    if (countryLocale?.currency) {
      return {
        decimal: countryLocale.currency.decimal || '.',
        group: countryLocale.currency.thousands || ',',
      };
    }
    // Default to US format if no locale info found
    return { decimal: '.', group: ',' };
  };

  const separators = getLocaleSeparators();

  const textFieldProps = {
    id,
    label,
    readOnly,
    supportText,
  };

  const intlConfig = subTenderType ? { currency: subTenderType, locale } : { currency, locale };

  return (
    <TextFieldStyled
      shouldMakeActive
      ariaLive="polite"
      error={!!error}
      errorMessage={error?.message}
      requiredIndicator={isRequired ? '*' : ''}
      slots={{
        InputSlot: (
          <CurrencyInput
            allowDecimals
            allowNegativeValue={allowNegativeValue}
            className="currency-input"
            decimalSeparator={separators.decimal}
            decimalsLimit={getDecimalCount()}
            defaultValue={value}
            disableAbbreviations
            disabled={readOnly}
            groupSeparator={separators.group}
            id={id}
            intlConfig={intlConfig}
            name={name}
            value={formattedValue}
            onBlur={onBlur}
            onValueChange={(newValue) => {
              if (!newValue) {
                setFormattedValue(0);
                onChange(0);
              } else {
                setFormattedValue(newValue);
                onChange(stringToFloat(newValue));
              }
            }}
            style={{ direction: 'ltr', textAlign: 'left' }}
          />
        ),
      }}
      supportTextProps={{ style: { color: supportTextColor } }}
      type="text"
      style={{ textAlign: 'initial' }}
      {...textFieldProps}
    />
  );
}

MultipleCurrencyInput.defaultProps = {
  allowNegativeValue: false,
  isRequired: false,
  readOnly: false,
  supportText: '',
  supportTextColor: '',
  subTenderType: '',
};

MultipleCurrencyInput.propTypes = {
  allowNegativeValue: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  control: PropTypes.any.isRequired,
  id: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  supportText: PropTypes.string,
  supportTextColor: PropTypes.string,
  subTenderType: PropTypes.string,
};
